import React from "react";
import { NTPIdentity } from "ntp-logo-react";
import { PAYMENT_PROCESSOR } from "../../constants/legal";

/**
 * The NETOPIA badge in the footer, which their point-of-sale approval requires.
 *
 * Mandatory, not decorative: the application was rejected once for not having
 * it, and their notice is explicit that it goes in the footer and must carry
 * the Visa and Mastercard marks. Their own React component is used rather than
 * a static image because it is what they hand out — the badge is served from
 * `mny.ro` with our merchant id on it, which is how they see that it is live.
 *
 * ─── The trap in their API ───────────────────────────────────────────────────
 * `color` is NOT the colour of the logo. It is the colour the logo is being
 * placed ON, and the component picks a light or dark artwork from it: pass
 * something bright and you get the BLACK logo, which on our dark footer would
 * be a bright plate with an invisible wordmark. Their sample passes "#ffffff"
 * because their sample sits on a white page.
 *
 * So this passes the footer's own background — `--color-ink-900` — and gets the
 * white artwork back, with the Visa and Mastercard marks in their own brand
 * colours. Change the footer background and this value has to follow it.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Imported by name, not as a default. The package is CommonJS, and its default
 * export arrives through Vite's interop as a module object rather than a
 * function — which React renders by throwing, taking the whole page with it.
 * That is not a hypothetical: it is what the first version of this file did.
 *
 * Sized by the wrapper, because their `<img>` is width:100%/height:100% with no
 * object-fit and will stretch to whatever box it lands in. 223x40 holds the
 * horizontal artwork's own 418x75 ratio, so nothing is squashed.
 */

/** The footer's background (`--color-ink-900`), which decides light or dark artwork. */
const FOOTER_BACKGROUND = "#1a1d19";

/**
 * Keeps a third party's component from taking the site down with it.
 *
 * This badge is somebody else's code, rendering somebody else's image, and it
 * has already white-screened every public page once during development. The
 * footer is on every one of them, so the blast radius of an upgrade going wrong
 * is the entire site — and the thing at risk is a logo. Failing to nothing is
 * strictly better, and NETOPIA notice a missing badge long before a customer
 * notices a missing page.
 */
class BadgeBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    // Logged rather than swallowed: the badge is a condition of being allowed
    // to take payments, so somebody has to find out it stopped rendering.
    console.error("NETOPIA footer badge failed to render", error);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const NetopiaBadge = () => (
  <BadgeBoundary>
    <div className="netopia-badge h-10 w-[223px] shrink-0">
      <NTPIdentity
        color={FOOTER_BACKGROUND}
        version="horizontal"
        secret={PAYMENT_PROCESSOR.logoWidgetId}
      />
    </div>
  </BadgeBoundary>
);

export default NetopiaBadge;
